import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, FieldArray, Form } from 'formik';
import * as Yup from 'yup';
import { Box, Text, Button, Modal } from '@rsos/components/capstone/base';
import InputText from '@rsos/components/capstone/base/form/inputs/InputText';
import { createAppRole, selectUserOrgByApp } from '@rsos/sinatra';
import {
  checkPremiumPermissions,
  notifyMissingRequiredPermissions,
} from '@rsos/utils/checkPremiumPermissions';
import { notifySuccess, notifyError } from '@rsos/utils/toastUtils';
import {
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '../../../base/Modal/ModalComponents';
import { ModalError, ModalLoading } from '../../Common/ModalInserts';
import { trackAddRole } from '../../rolesTrackings';
import { dividePermissionArrayByType } from '../../utils/dividePermissionArrayByType';
import { getAvatarBg } from '../../utils/getAvatarBg';
import { ScrollableContainer } from './AddRoleModal.styles';
import SectionForm from './SectionForm';

const schema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .matches(/^[a-zA-Z0-9'&) (+.-]*$/, 'The Role Title is invalid') //check for alphanumeric and special chars
    .matches(/^(?=.*[a-zA-Z0-9]).+$/, 'The Role Title is invalid') //check for at least 1 alphanumeric
    .required('The Role Title is required')
    .min(1, 'The Role Title has to be at least 1 char long')
    .max(150, 'The Role Title is too long'),
});
const initialStateRequestStatus = {
  loading: false,
  idle: true,
  error: null,
};

// fetchStatus - loading state when opening the modal
const AddRoleModal = ({
  isOpened,
  onClose,
  permissions,
  fetchStatus,
  refetchPermissions,
}) => {
  const sinatra = useSelector(state => state.sinatra);
  const { irpVersion } = useSelector(state => state.sinatra.irp);

  const dispatch = useDispatch();
  const { application } = sinatra.user.currentRole;
  const org = selectUserOrgByApp(sinatra, application);

  const [pageAccessPermissions, dataSourcesPermissions] =
    dividePermissionArrayByType(permissions, e => e.rsp_rbac);

  const [requestStatus, setRequestStatus] = useState(initialStateRequestStatus); // controls Button's loading state

  const handleCheckbox = (e, arrayHelpers, permission, values) => {
    if (e.target.checked) arrayHelpers.push(permission.name);
    else {
      const idx = values.permissions.indexOf(permission.name);
      arrayHelpers.remove(idx);
    }
  };
  const handleClose = handleReset => {
    handleReset();
    setRequestStatus(initialStateRequestStatus);
    onClose();
  };
  const handleAddRole = (values, resetForm) => {
    const newRolePayload = {
      permissions: values.permissions,
      name: values.name.trim(),
      avatar_bg_color: getAvatarBg(),
    };

    const missingRequiredPermissions = checkPremiumPermissions({
      assignedPermissions: newRolePayload.permissions,
    });

    if (missingRequiredPermissions) {
      notifyMissingRequiredPermissions();
      return;
    }

    setRequestStatus({ loading: true, idle: false, error: null });
    dispatch(createAppRole(org.id, application, newRolePayload))
      .then(() => {
        notifySuccess('Role added.');
        setRequestStatus(initialStateRequestStatus);
        trackAddRole(values.name, irpVersion);
        resetForm();
        onClose();
      })
      .catch(e => {
        const error = e.error?.response?.data?.detail || 'There was an error';
        notifyError(error);
        setRequestStatus({ loading: false, idle: false, error: error });
      });
  };
  const handleRetry = () => {
    setRequestStatus(initialStateRequestStatus);
  };

  const getModalInsert = (fetchStatus, requestStatus, handleReset) => {
    if (fetchStatus.loading) {
      return <ModalLoading />;
    } else if (fetchStatus.error) {
      return (
        <ModalError
          onClick={refetchPermissions}
          onClose={onClose}
          error={fetchStatus.error}
        />
      );
    } else if (requestStatus.error) {
      return (
        <ModalError
          onClick={handleRetry}
          onClose={() => handleClose(handleReset)}
          error={requestStatus.error}
        />
      );
    }
  };
  return (
    <Formik
      initialValues={{ permissions: [], name: '' }}
      enableReinitialize
      validationSchema={schema}
      onSubmit={async (values, { resetForm }) => {
        await handleAddRole(values, resetForm);
      }}
    >
      {({
        values,
        handleBlur,
        handleChange,
        handleReset,
        errors,
        touched,
        setFieldValue,
        isValid,
      }) => (
        <Modal
          isOpened={isOpened}
          onClose={() => handleClose(handleReset)}
          width={400}
          id="modal-add-role"
          disableBackdropClick
        >
          {requestStatus.idle && fetchStatus.idle ? (
            <>
              <ModalHeader onClose={() => handleClose(handleReset)}>
                <Box width={400}>
                  <Text size="xlarge" color="primaryText">
                    Add New Role
                  </Text>
                </Box>
              </ModalHeader>
              <ModalBody>
                <Form id="addRoleForm">
                  <InputText
                    name="name"
                    id="name"
                    placeholder="Add new role title here"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.name && errors.name ? errors.name : null}
                    setFieldValue={setFieldValue}
                    shouldErrorTakeSpace
                  />
                  <Text my="10px" size="normal" color="secondaryText">
                    This role has access to the following:
                  </Text>
                  <ScrollableContainer>
                    <FieldArray name="permissions">
                      {arrayHelpers => (
                        <div
                          id="add-role-checkbox-group"
                          aria-labelledby="add-role-checkbox-group"
                          role="group"
                        >
                          <SectionForm
                            permissions={pageAccessPermissions}
                            values={values}
                            arrayHelpers={arrayHelpers}
                            onChangeCheckbox={handleCheckbox}
                            title="Page Access"
                          />

                          <SectionForm
                            permissions={dataSourcesPermissions}
                            values={values}
                            arrayHelpers={arrayHelpers}
                            onChangeCheckbox={handleCheckbox}
                            title="Data Access"
                          />
                        </div>
                      )}
                    </FieldArray>
                  </ScrollableContainer>
                </Form>
              </ModalBody>
              <ModalFooter flow={12}>
                <Button
                  onClick={() => {}}
                  grow
                  noMargin
                  type="submit"
                  form="addRoleForm"
                  disabled={!isValid || !touched.name}
                  isLoadingProp={requestStatus === 'loading'}
                >
                  Save
                </Button>
              </ModalFooter>
            </>
          ) : (
            getModalInsert(fetchStatus, requestStatus, handleReset)
          )}
        </Modal>
      )}
    </Formik>
  );
};

export default AddRoleModal;
